import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Popover,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";

import {
  OrderedListOutlined,
  PlusOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const allowedIndicators = [
  "RSI",
  "OBV",
  "StochasticOscillator",
  "WilliamsRange",
  "PROC",
  // "MACD", deprecated - replaced by MACDP
  "MACDP",
  "BollingerB",
  "STDP",
  "VSTD",
  "ATRP",
  "CMF",
  // "ADX",
  "VROC",
];

const defaultValues = {
  RSI: 14,
  OBV: 9,
  StochasticOscillator: 14,
  WilliamsRange: 14,
  PROC: 9,
  BollingerB: 20,
  STDP: 20,
  VSTD: 30,
  ATRP: 14,
  CMF: 20,
  ADX: 14,
  VROC: 10,
};

const macdDefaults = { short_period: 12, long_period: 26, macd_period: 9 };
const TEST_INDICATOR_KEY = "__test_indicator__";

const IndicatorsSelector = () => {
  const [wantedIndicators, setWantedIndicators] = useState([]);
  const [openPopover, setOpenPopover] = useState(null);
  const [popoverValue, setPopoverValue] = useState(null);
  const [popoverMACDValues, setPopoverMACDValues] = useState(macdDefaults);
  const [testIndicatorName, setTestIndicatorName] = useState("");
  const [popoverError, setPopoverError] = useState("");
  const [tagSortOrder, setTagSortOrder] = useState("addOrder");

  const nameCountersRef = useRef({});
  const handleAddRef = useRef(null);
  const form = Form.useFormInstance();
  const { token } = theme.useToken();

  const isMACDType = (name) => name === "MACD" || name === "MACDP";

  const openIndicatorPopover = (name) => {
    setOpenPopover(name);
    setPopoverError("");
    if (isMACDType(name)) {
      setPopoverMACDValues({ ...macdDefaults });
    } else {
      setPopoverValue(defaultValues[name] ?? null);
    }
  };

  const openTestIndicatorPopover = () => {
    setOpenPopover(TEST_INDICATOR_KEY);
    setPopoverError("");
    setTestIndicatorName("");
    setPopoverValue(null);
  };

  const closePopover = () => {
    setOpenPopover(null);
    setPopoverError("");
  };

  const isDuplicate = (name, value) => {
    const allIndicators = form.getFieldValue("indicators") || {};
    const existingValues = Object.values(allIndicators[name] || {});
    if (typeof value === "object") {
      return existingValues.some(
        (v) =>
          v &&
          v.short_period === value.short_period &&
          v.long_period === value.long_period &&
          v.macd_period === value.macd_period,
      );
    }
    return existingValues.some((v) => v === value);
  };

  const addIndicator = (name, value, displayValue) => {
    const index = nameCountersRef.current[name] ?? 0;
    nameCountersRef.current[name] = index + 1;

    form.setFieldValue(["indicators", name, index], value);

    setWantedIndicators((prev) => [
      ...prev,
      { id: `${name}_${index}`, name, index, displayValue },
    ]);

    closePopover();
  };

  const handleAdd = (name) => {
    if (name === TEST_INDICATOR_KEY) {
      const trimmedName = testIndicatorName.trim();
      if (!trimmedName) {
        setPopoverError("Please enter a name");
        return;
      }
      if (popoverValue === null || popoverValue === undefined) {
        setPopoverError("Please enter a period");
        return;
      }
      if (isDuplicate(trimmedName, popoverValue)) {
        setPopoverError(`This ${trimmedName} configuration already exists`);
        return;
      }
      addIndicator(trimmedName, popoverValue, String(popoverValue));
      return;
    }

    const value = isMACDType(name) ? { ...popoverMACDValues } : popoverValue;

    if (value === null || value === undefined) {
      setPopoverError("Please enter a value");
      return;
    }

    if (isDuplicate(name, value)) {
      setPopoverError(`This ${name} configuration already exists`);
      return;
    }

    const displayValue = isMACDType(name)
      ? `${value.short_period} / ${value.long_period} / ${value.macd_period}`
      : String(value);

    addIndicator(name, value, displayValue);
  };

  handleAddRef.current = handleAdd;

  useEffect(() => {
    if (!openPopover) return;

    const onKeyDown = (e) => {
      if (e.key !== "Enter") return;
      if (
        e.target instanceof HTMLElement &&
        e.target.closest("[data-indicator-popover-cancel]")
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handleAddRef.current(openPopover);
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [openPopover]);

  const removeIndicator = (id, name, index) => {
    setWantedIndicators((prev) => prev.filter((item) => item.id !== id));
    const current = { ...(form.getFieldValue(["indicators", name]) || {}) };
    delete current[index];
    form.setFieldValue(["indicators", name], current);
  };

  const getTestIndicatorPopoverContent = () => (
    <div className="flex flex-col gap-3" style={{ minWidth: 180 }}>
      <div className="flex flex-col gap-1">
        <span className="text-sm">Name</span>
        <Input
          value={testIndicatorName}
          onChange={(e) => setTestIndicatorName(e.target.value)}
          onPressEnter={() => handleAdd(TEST_INDICATOR_KEY)}
          placeholder="Indicator name"
          autoFocus
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm">Period</span>
        <InputNumber
          value={popoverValue}
          onChange={(v) => setPopoverValue(v)}
          onPressEnter={() => handleAdd(TEST_INDICATOR_KEY)}
          style={{ width: "100%" }}
        />
      </div>
      {popoverError && (
        <span className="text-red-500 text-xs">{popoverError}</span>
      )}
      <div className="flex gap-2 justify-end">
        <Button
          size="small"
          data-indicator-popover-cancel
          onClick={closePopover}
        >
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={() => handleAdd(TEST_INDICATOR_KEY)}
        >
          Add
        </Button>
      </div>
    </div>
  );

  const getPopoverContent = (name) => (
    <div className="flex flex-col gap-3" style={{ minWidth: 180 }}>
      {isMACDType(name) ? (
        <>
          <div className="flex flex-col gap-1">
            <span className="text-sm">Short Period</span>
            <InputNumber
              value={popoverMACDValues.short_period}
              onChange={(v) =>
                setPopoverMACDValues((prev) => ({ ...prev, short_period: v }))
              }
              onPressEnter={() => handleAdd(name)}
              style={{ width: "100%" }}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm">Long Period</span>
            <InputNumber
              value={popoverMACDValues.long_period}
              onChange={(v) =>
                setPopoverMACDValues((prev) => ({ ...prev, long_period: v }))
              }
              onPressEnter={() => handleAdd(name)}
              style={{ width: "100%" }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm">MACD Period</span>
            <InputNumber
              value={popoverMACDValues.macd_period}
              onChange={(v) =>
                setPopoverMACDValues((prev) => ({ ...prev, macd_period: v }))
              }
              onPressEnter={() => handleAdd(name)}
              style={{ width: "100%" }}
            />
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="text-sm">Period</span>
          <InputNumber
            value={popoverValue}
            onChange={(v) => setPopoverValue(v)}
            onPressEnter={() => handleAdd(name)}
            style={{ width: "100%" }}
            autoFocus
          />
        </div>
      )}
      {popoverError && (
        <span className="text-red-500 text-xs">{popoverError}</span>
      )}
      <div className="flex gap-2 justify-end">
        <Button
          size="small"
          data-indicator-popover-cancel
          onClick={closePopover}
        >
          Cancel
        </Button>
        <Button size="small" type="primary" onClick={() => handleAdd(name)}>
          Add
        </Button>
      </div>
    </div>
  );

  const selectedIndicatorNames = new Set(
    wantedIndicators.map((item) => item.name),
  );

  const displayedIndicators = useMemo(() => {
    if (tagSortOrder === "name") {
      return [...wantedIndicators].sort((a, b) => a.name.localeCompare(b.name));
    }
    return wantedIndicators;
  }, [wantedIndicators, tagSortOrder]);

  return (
    <div className="basis-[60%]">
      <Divider orientation="left">Select Indicators</Divider>
      <div className="flex flex-wrap gap-2 mb-4">
        {allowedIndicators.map((indicator, idx) => {
          const isSelected = selectedIndicatorNames.has(indicator);

          return (
            <Popover
              key={idx}
              trigger="click"
              open={openPopover === indicator}
              onOpenChange={(open) => {
                if (open) openIndicatorPopover(indicator);
                else closePopover();
              }}
              content={getPopoverContent(indicator)}
              title={`Configure ${indicator}`}
            >
              <Button
                type="dashed"
                style={
                  isSelected ? { borderColor: token.colorPrimary } : undefined
                }
                icon={<PlusOutlined />}
              >
                {indicator}
              </Button>
            </Popover>
          );
        })}
        <Popover
          trigger="click"
          open={openPopover === TEST_INDICATOR_KEY}
          onOpenChange={(open) => {
            if (open) openTestIndicatorPopover();
            else closePopover();
          }}
          content={getTestIndicatorPopoverContent()}
          title="Configure Test Indicator"
        >
          <Button type="dashed" icon={<PlusOutlined />}>
            Test Indicator
          </Button>
        </Popover>
      </div>
      <div
        className="rounded-lg p-3"
        style={{
          background: token.colorFillAlter,
          border: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <Text type="secondary" className="text-sm">
            Selected indicators
            {wantedIndicators.length > 0 && (
              <span
                style={{ color: token.colorText }}
              >{` (${wantedIndicators.length})`}</span>
            )}
          </Text>
          {wantedIndicators.length > 1 && (
            <Tooltip
              title={
                tagSortOrder === "addOrder"
                  ? "Sort alphabetically"
                  : "Sort by add order"
              }
            >
              <Button
                type="text"
                size="small"
                aria-label={
                  tagSortOrder === "addOrder"
                    ? "Sort alphabetically"
                    : "Sort by add order"
                }
                icon={
                  tagSortOrder === "addOrder" ? (
                    <SortAscendingOutlined />
                  ) : (
                    <OrderedListOutlined />
                  )
                }
                onClick={() =>
                  setTagSortOrder((prev) =>
                    prev === "addOrder" ? "name" : "addOrder",
                  )
                }
              />
            </Tooltip>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {wantedIndicators.length === 0 ? (
            <Text type="secondary" className="text-xs">
              Click an indicator above to add it.
            </Text>
          ) : (
            displayedIndicators.map(({ id, name, index, displayValue }) => (
              <Tag
                key={id}
                closable
                onClose={() => removeIndicator(id, name, index)}
              >
                {name}: {displayValue}
              </Tag>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default IndicatorsSelector;
